import { supabase } from "@/platform/lib/supabase";

export interface SessionHistoryRow {
  id: string;
  kind: string;
  finished_at: string | null;
  total_questions: number;
  correct_count: number;
  byDiscipline: Record<string, { total: number; correct: number }>;
}

export async function fetchUspSessionHistory(userId: string): Promise<SessionHistoryRow[]> {
  const { data, error } = await supabase
    .from("usp_sessions")
    .select("id, kind, finished_at, total_questions, correct_count, metadata")
    .eq("user_id", userId)
    .not("finished_at", "is", null)
    .order("finished_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as Array<{
    id: string;
    kind: string;
    finished_at: string | null;
    total_questions: number;
    correct_count: number;
    metadata: { by_discipline?: Record<string, { total: number; correct: number }> } | null;
  }>).map((s) => ({
    id: s.id,
    kind: s.kind,
    finished_at: s.finished_at,
    total_questions: s.total_questions,
    correct_count: s.correct_count,
    byDiscipline: s.metadata?.by_discipline ?? {},
  }));
}

/**
 * Prática avulsa de discursivas/redação não cria sessão — fica solta em
 * usp_attempts. Sem isto, 2ª fase e redação somem da evolução. Agregamos por
 * disciplina (via join com a questão) e por média de score.
 */
export interface DiscursivePracticeRow {
  discipline: string;
  attempts: number;
  /** Média do score bruto (pontos Fuvest da questão, ex.: 6.0 de 10). */
  avgScore: number;
}

export async function fetchUspDiscursivePractice(
  userId: string,
): Promise<DiscursivePracticeRow[]> {
  const { data, error } = await supabase
    .from("usp_attempts")
    .select("score, is_correct, usp_questions!inner(discipline, kind)")
    .eq("user_id", userId)
    .is("session_id", null)
    .eq("usp_questions.kind", "discursive");
  if (error) throw error;

  const rows = (data ?? []) as unknown as Array<{
    score: number | null;
    is_correct: boolean | null;
    usp_questions: { discipline: string; kind: string } | { discipline: string; kind: string }[] | null;
  }>;

  const map = new Map<string, { attempts: number; scoreSum: number; scored: number }>();
  for (const r of rows) {
    const q = Array.isArray(r.usp_questions) ? r.usp_questions[0] : r.usp_questions;
    const disc = q?.discipline;
    if (!disc) continue;
    const b = map.get(disc) ?? { attempts: 0, scoreSum: 0, scored: 0 };
    b.attempts += 1;
    if (typeof r.score === "number") {
      b.scoreSum += r.score;
      b.scored += 1;
    }
    map.set(disc, b);
  }
  return Array.from(map.entries())
    .map(([discipline, b]) => ({
      discipline,
      attempts: b.attempts,
      avgScore: b.scored > 0 ? Math.round((b.scoreSum / b.scored) * 10) / 10 : 0,
    }))
    .sort((a, b) => a.avgScore - b.avgScore);
}

export interface DisciplineAccuracy {
  discipline: string;
  total: number;
  correct: number;
  pct: number;
}

export function aggregateDisciplineAccuracy(sessions: SessionHistoryRow[]): DisciplineAccuracy[] {
  const map = new Map<string, { total: number; correct: number }>();
  for (const s of sessions) {
    for (const [disc, stat] of Object.entries(s.byDiscipline)) {
      const b = map.get(disc) ?? { total: 0, correct: 0 };
      b.total += stat.total;
      b.correct += stat.correct;
      map.set(disc, b);
    }
  }
  return Array.from(map.entries())
    .map(([discipline, s]) => ({
      discipline,
      total: s.total,
      correct: s.correct,
      pct: s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0,
    }))
    .sort((a, b) => a.pct - b.pct);
}
